import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SwUpdate } from '@angular/service-worker';
import { filter, interval } from 'rxjs';

/**
 * Watches for new service worker versions (e.g. updated e-invoice format
 * packages under assets/formats) and offers the user a non-intrusive
 * "reload" prompt instead of silently staying on stale cached assets.
 */
@Injectable({
  providedIn: 'root',
})
export class ServiceWorkerUpdateService {
  private static readonly CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000;

  private readonly swUpdate = inject(SwUpdate, { optional: true });
  private readonly snackBar = inject(MatSnackBar);
  private initialized = false;
  private promptVisible = false;

  init(): void {
    if (this.initialized || !this.swUpdate?.isEnabled) {
      return;
    }
    this.initialized = true;

    this.swUpdate.versionUpdates
      .pipe(filter((event) => event.type === 'VERSION_READY'))
      .subscribe(() => this.promptReload());

    // The client is in a broken state (e.g. cached assets no longer
    // available); a hard reload is the only way to recover.
    this.swUpdate.unrecoverable.subscribe(() => {
      window.location.reload();
    });

    void this.swUpdate.checkForUpdate();

    interval(ServiceWorkerUpdateService.CHECK_INTERVAL_MS).subscribe(() => {
      void this.swUpdate?.checkForUpdate();
    });
  }

  private promptReload(): void {
    if (this.promptVisible) {
      return;
    }
    this.promptVisible = true;

    const message = $localize`:@@app.update.available:A new version is available.`;
    const action = $localize`:@@app.update.reload:Reload`;

    this.snackBar
      .open(message, action)
      .onAction()
      .subscribe(() => {
        window.location.reload();
      });
  }
}
